// VPS & Terminals — collector/host health from the /vps and /terminals snapshots plus SSE
// `health` events. Shows one card per VPS (collector version, freshness, spool/queue/drops)
// and the terminal inventory table.

import { useState } from 'react';
import * as api from '../api/client';
import { ApiError } from '../api/http';
import { C, MONO } from '../lib/tokens';
import { fmtAge, fmtTS, parseTs } from '../lib/format';
import { chipS, mTd, mTh, mono, GBTN_SM } from '../lib/styles';
import { EmptyState } from '../components/common';
import { useAuth, useData, useServerNow } from '../store/hooks';
import { feed } from '../store/dataStore';
import { toastMsg } from '../store/uiStore';

const DANGER_SM: React.CSSProperties = { ...GBTN_SM, color: C.rd, border: '1px solid ' + C.rd + '77' };

function statusColor(status: string): string {
  const s = status.toLowerCase();
  if (s === 'live' || s === 'online' || s === 'connected') return C.gn;
  if (s.includes('off') || s.includes('unreachable') || s.includes('down')) return C.rd;
  if (s.includes('stale') || s.includes('unknown')) return C.gy;
  return C.am;
}

function fmtBytes(n: number): string {
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / (1024 * 1024)).toFixed(1) + ' MB';
}

export function VpsTerminals() {
  const vpsList = useData((s) => s.vps);
  const terminals = useData((s) => s.terminals);
  const serverNow = useServerNow();
  const isAdmin = useAuth((s) => s.user?.role === 'admin');

  // Admin decommission: which VPS is awaiting confirmation, and which one is in flight.
  const [confirmVps, setConfirmVps] = useState<string | null>(null);
  const [busyVps, setBusyVps] = useState<string | null>(null);

  async function decommission(vpsId: string): Promise<void> {
    if (busyVps) return;
    setBusyVps(vpsId);
    try {
      const res = await api.decommissionVps(vpsId);
      const d = res.deleted;
      toastMsg(
        `Decommissioned ${vpsId} — removed ${d.terminals} terminal(s), ${d.accounts} account(s), ${d.alerts} alert(s).`,
      );
      setConfirmVps(null);
      await feed.resnapshot();
    } catch (e) {
      toastMsg('Could not decommission ' + vpsId + ': ' + (e instanceof ApiError ? e.message : 'error'));
    } finally {
      setBusyVps(null);
    }
  }

  return (
    <section data-screen-label="Collectors & terminals" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <h1 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Collectors &amp; Terminals</h1>

      {vpsList.length === 0 && <EmptyState title="No VPS registered" sub="No collector has reported in yet." />}

      {vpsList.map((v) => {
        const color = statusColor(v.status);
        const lastSeen = parseTs(v.last_seen);
        const meta: Array<[string, string]> = [
          ['VPS', v.vps_id],
          ['Agent', v.agent_id],
          ['Collector', v.collector_version],
          ['Last snapshot', lastSeen ? fmtAge(serverNow - lastSeen) + ' ago' : 'never'],
          ['Terminals', String(v.terminals)],
          ['Spool', fmtBytes(v.spool_bytes)],
          ['Queue depth', String(v.queue_depth)],
          ['Dropped samples', String(v.dropped_samples)],
        ];
        return (
          <div key={v.vps_id} style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderBottom: '1px solid ' + C.ln2, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>{v.vps_id}</h2>
              <span style={{ fontSize: 11, color: C.dm }}>{v.terminals + ' terminals · collector ' + v.collector_version}</span>
              <div style={{ flex: 1 }} />
              <span style={chipS(color)}>
                {color === C.gn ? '●' : color === C.rd ? '✕' : '◌'} {v.status}
              </span>
              {isAdmin &&
                (confirmVps === v.vps_id ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <span style={{ fontSize: 10.5, color: C.rd }}>Decommission {v.vps_id}?</span>
                    <button onClick={() => decommission(v.vps_id)} disabled={busyVps === v.vps_id} style={{ ...DANGER_SM, opacity: busyVps === v.vps_id ? 0.6 : 1 }}>
                      {busyVps === v.vps_id ? '…' : 'Yes, delete'}
                    </button>
                    <button onClick={() => setConfirmVps(null)} disabled={busyVps === v.vps_id} style={GBTN_SM}>Cancel</button>
                  </span>
                ) : (
                  <button
                    onClick={() => setConfirmVps(v.vps_id)}
                    disabled={!!busyVps}
                    title="Decommission this VPS — removes its terminals, accounts, history and alerts"
                    aria-label={'Decommission ' + v.vps_id}
                    style={{ ...DANGER_SM, opacity: busyVps ? 0.6 : 1 }}
                  >
                    Decommission
                  </button>
                ))}
            </div>
            {isAdmin && confirmVps === v.vps_id && (
              <div role="alert" style={{ fontSize: 11, color: C.am, background: 'rgba(224,168,62,.08)', borderBottom: '1px solid ' + C.ln2, padding: '8px 14px', lineHeight: 1.5 }}>
                Decommission <strong>{v.vps_id}</strong>? This removes its terminals, accounts, history, and alerts. The collector can re-register if it sends telemetry again.
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
              <div style={{ flex: '1.2 1 300px', minWidth: 280, padding: '10px 14px' }}>
                {meta.map(([k, val]) => (
                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '4px 0', borderBottom: '1px solid #16203A' }}>
                    <span style={{ fontSize: 11, color: C.dm }}>{k}</span>
                    <span style={{ fontSize: 11, fontFamily: MONO, textAlign: 'right' }}>{val}</span>
                  </div>
                ))}
              </div>
              <div style={{ flex: '1 1 260px', minWidth: 240, padding: 14, display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center' }}>
                <HealthBar label="Spool" value={v.spool_bytes} max={50 * 1024 * 1024} text={fmtBytes(v.spool_bytes)} warnFrac={0.6} />
                <HealthBar label="Queue" value={v.queue_depth} max={4096} text={String(v.queue_depth)} warnFrac={0.6} />
                <HealthBar label="Drops" value={v.dropped_samples} max={Math.max(100, v.dropped_samples)} text={String(v.dropped_samples)} warnFrac={0.01} />
                <div style={{ fontSize: 10, color: C.dim2 }}>Host telemetry from collector snapshots</div>
              </div>
            </div>
          </div>
        );
      })}

      <div style={{ background: C.s1, border: '1px solid ' + C.ln, borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '10px 14px', borderBottom: '1px solid ' + C.ln2 }}>
          <h2 style={{ margin: 0, fontSize: 12.5, fontWeight: 600 }}>Terminals</h2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
            <thead>
              <tr>
                {['Terminal', 'VPS', 'Account', 'EA', 'Build', 'Boot', 'Last seen', 'Status'].map((h, i) => (
                  <th key={h} scope="col" style={mTh(i >= 4 && i <= 5 ? 'right' : 'left')}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {terminals
                .slice()
                .sort((a, b) => (a.terminal_id < b.terminal_id ? -1 : 1))
                .map((t) => {
                  const lastSeen = parseTs(t.last_seen);
                  const color = statusColor(t.status);
                  return (
                    <tr key={t.terminal_id}>
                      <td style={mTd('left', mono())}>{t.terminal_id}</td>
                      <td style={mTd('left', mono(C.dm))}>{t.vps_id}</td>
                      <td style={mTd('left', { fontWeight: 600 })}>{t.account_label}</td>
                      <td style={mTd('left', mono(C.b9))}>{t.ea_version}</td>
                      <td style={mTd('right', mono(C.b9))}>{t.terminal_build}</td>
                      <td style={mTd('right', mono(C.dm))}>{t.boot_id.slice(0, 10)}</td>
                      <td style={mTd('left', mono(C.b9))}>{lastSeen ? fmtAge(serverNow - lastSeen) + ' ago' : 'never'}</td>
                      <td style={mTd('left', mono(color))}>{(color === C.gn ? '● ' : color === C.rd ? '✕ ' : '◌ ') + t.status}</td>
                    </tr>
                  );
                })}
              {terminals.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ ...mTd('left', { color: C.dm, textAlign: 'center' }), padding: 18 }}>
                    No terminals reported.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ fontSize: 10.5, color: C.dm, fontFamily: MONO }}>Last updated {fmtTS(serverNow)} (server time)</div>
    </section>
  );
}

function HealthBar({ label, value, max, text, warnFrac }: { label: string; value: number; max: number; text: string; warnFrac: number }) {
  const frac = Math.max(0, Math.min(1, value / max));
  const color = frac >= 0.85 ? C.rd : frac >= warnFrac ? C.am : C.bl;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '48px 1fr 60px', gap: 8, alignItems: 'center', fontSize: 11 }}>
      <span style={{ color: C.dm }}>{label}</span>
      <div style={{ height: 8, background: '#16223A', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: Math.round(frac * 100) + '%', height: '100%', borderRadius: 3, background: color }} />
      </div>
      <span style={{ fontFamily: MONO, textAlign: 'right' }}>{text}</span>
    </div>
  );
}
